import React from 'react';
import { useNavigate } from 'react-router-dom';
const API_URL = process.env.REACT_APP_API_URL || '/api';
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || window.location.origin;
const styles = {
  global: `
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif;
      background: #1a1a2e;
      min-height: 100vh;
      color: white;
    }
    
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
    }
    
    @keyframes pulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(124, 152, 133, 0.4); }
      50% { box-shadow: 0 0 0 20px rgba(124, 152, 133, 0); }
    }
  `,

  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    position: 'relative',
    overflow: 'hidden',
    background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 50%, #1a1a2e 100%)',
  },

  // Background decorations
  bgGlow1: {
    position: 'absolute',
    width: '600px',
    height: '600px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(124, 152, 133, 0.15) 0%, transparent 70%)',
    top: '-200px',
    right: '-100px',
    pointerEvents: 'none',
  },

  bgGlow2: {
    position: 'absolute',
    width: '500px',
    height: '500px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(147, 180, 186, 0.1) 0%, transparent 70%)',
    bottom: '-150px',
    left: '-100px',
    pointerEvents: 'none',
  },

  content: {
    maxWidth: '900px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '40px',
    position: 'relative',
    zIndex: 1,
    animation: 'fadeIn 0.8s ease-out',
  },

  // Main headline
  headline: {
    textAlign: 'center',
  },

  h1: {
    fontSize: 'clamp(36px, 6vw, 64px)',
    fontWeight: '700',
    color: 'white',
    lineHeight: '1.2',
    marginBottom: '0',
  },

  h1Span: {
    display: 'block',
    color: '#7c9885',
    marginTop: '8px',
  },

  // Video section
  videoContainer: {
    width: '100%',
    maxWidth: '800px',
    aspectRatio: '16 / 9',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    background: '#000',
  },

  videoPlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #2a2a4a 0%, #1a1a2e 100%)',
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: '18px',
    gap: '12px',
  },

  videoIcon: {
    fontSize: '48px',
    opacity: '0.5',
  },

  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
  },

  // Subtitle
  h2: {
    fontSize: 'clamp(18px, 3vw, 24px)',
    fontWeight: '400',
    color: 'rgba(255, 255, 255, 0.8)',
    textAlign: 'center',
    maxWidth: '600px',
    lineHeight: '1.6',
  },

  // CTA Button
  ctaButton: {
    padding: '18px 48px',
    fontSize: '18px',
    fontWeight: '600',
    color: 'white',
    background: 'linear-gradient(135deg, #7c9885 0%, #5a7a65 100%)',
    border: 'none',
    borderRadius: '50px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontFamily: 'inherit',
    boxShadow: '0 8px 30px rgba(124, 152, 133, 0.4)',
    animation: 'pulse 2s ease-in-out infinite',
  },

  ctaButtonHover: {
    transform: 'translateY(-3px)',
    boxShadow: '0 12px 40px rgba(124, 152, 133, 0.5)',
  },

  // Footer text
  footer: {
    marginTop: '40px',
    fontSize: '14px',
    color: 'rgba(255, 255, 255, 0.4)',
  },
};

function LandingPage() {
  const navigate = useNavigate();
  const [isHovering, setIsHovering] = React.useState(false);

  // Replace this with your actual YouTube video ID when ready
  const youtubeVideoId = null; // e.g., 'dQw4w9WgXcQ'

  const handleStartHiring = () => {
    navigate('/auth');
  };

  return (
    <>
      <style>{styles.global}</style>
      
      <div style={styles.container}>
        {/* Background glows */}
        <div style={styles.bgGlow1}></div>
        <div style={styles.bgGlow2}></div>

        <div style={styles.content}>
          {/* Main Headline */}
          <div style={styles.headline}>
            <h1 style={styles.h1}>
              Hire the best talent
              <span style={styles.h1Span}>in 60 seconds</span>
            </h1>
          </div>

          {/* Video Section */}
          <div style={styles.videoContainer}>
            {youtubeVideoId ? (
              <iframe
                style={styles.iframe}
                src={`https://www.youtube.com/embed/${youtubeVideoId}`}
                title="Instaview Demo"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div style={styles.videoPlaceholder}>
                <span style={styles.videoIcon}>▶️</span>
                <span>Video coming soon</span>
              </div>
            )}
          </div>

          {/* Subtitle */}
          <h2 style={styles.h2}>
            Instaview is a hiring match-making platform that gives everyone a level playing field.
          </h2>

          {/* CTA Button */}
          <button
            style={{
              ...styles.ctaButton,
              ...(isHovering ? styles.ctaButtonHover : {})
            }}
            onClick={handleStartHiring}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            Start hiring now!
          </button>

          {/* Optional footer */}
          <div style={styles.footer}>
            No credit card required • Free to get started
          </div>
        </div>
      </div>
    </>
  );
}

export default LandingPage;